import React, { useCallback, useMemo, useRef } from 'react';
import noop from 'lodash/noop';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Stack, Typography } from '@lyearn/ui';
import Form, { FieldOverrides, FormProps } from '@/components/Form';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import useScrollProgress from '@/hooks/useScrollProgress';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { adaptToUpdateQuestionEntity } from '@/modules/corporate/modules/quiz/views/QuizEditor/utils/adapters';
import {
  getUSurveyBankQuestionSettingsFormProps,
  getUSurveyBankQuestionSettingsInitialValues,
} from '@/modules/user-survey/views/UserSurveyEditor/components/QuestionsView/components/UserSurveyQuestionSettings/config/question-bank';
import { QuestionApplicableOnEnum } from '@/types/schema';

import {
  getQuizQuestionSettingsFormConfig,
  getQuizQuestionSettingsInitialValues,
} from './utils/config';
import { settingsContainerStyles } from './styles';
import { QuestionSettingsProps } from './types';

const OVERRIDES: FieldOverrides = {
  Section: {
    spacing: 'space-y-32',
    Name: { variant: 'heading-xxs' },
  },
};

const QuestionsSettings: React.FC<QuestionSettingsProps> = (props) => {
  const { t } = useTranslation();
  const { question, onChange, applicableOn, viewMode, accessType, fullWidth } = props;
  const user = useUserContext();
  const { [FeatureFlags.EnableSubWorkspacesForQuestions]: isQuestionSubWorkspacesEnabled } =
    useFeatureFlags();

  const formProps: Pick<FormProps, 'initialValues' | 'fields' | 'sectionLayout'> = useMemo(() => {
    if (applicableOn === QuestionApplicableOnEnum.Quiz) {
      return {
        initialValues: getQuizQuestionSettingsInitialValues(question),
        ...getQuizQuestionSettingsFormConfig({
          t,
          ...props,
          allowPartialUpdates: viewMode === 'partialUpdate',
          accessType,
          isQuestionSubWorkspacesEnabled,
        }),
      };
    }

    return {
      initialValues: getUSurveyBankQuestionSettingsInitialValues({ question }),
      ...getUSurveyBankQuestionSettingsFormProps({
        t,
        viewMode,
        applicableOn,
        questionType: question.questionType,
        subType: question.subType,
        isQuestionSubWorkspacesEnabled,
        accessType,
      }),
    };
  }, [accessType, applicableOn, isQuestionSubWorkspacesEnabled, props, question, t, viewMode]);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const { scrollProgress } = useScrollProgress(scrollContainerRef.current);

  const _onChange = useCallback(
    ({ values }: any) => {
      const adaptedUpdateEntityObject = adaptToUpdateQuestionEntity({
        question,
        values,
        t,
        applicableOn,
        currentSubWorkspace: user.currentSubWorkspace,
      });
      onChange(adaptedUpdateEntityObject);
    },
    [applicableOn, onChange, question, t, user.currentSubWorkspace],
  );

  return (
    <Stack
      ref={scrollContainerRef}
      className={cn(
        'overflow-y-auto overflow-x-hidden',
        fullWidth ? 'w-full' : settingsContainerStyles,
      )}
      direction="col">
      <Typography
        className={cn('sticky top-0 z-10 bg-bg-primary px-24 pt-24 pb-16 capitalize', {
          'border-b border-divider': scrollProgress > 0,
        })}
        variant="heading-xs">
        {t('QuizCreator:QuestionsSettings.QuestionsSettings')}
      </Typography>
      <Box className="px-16 pt-8 pb-48">
        <Form
          formName="QuestionBankCreator"
          overrides={OVERRIDES}
          onChange={_onChange}
          onSubmit={noop}
          {...formProps}
        />
      </Box>
    </Stack>
  );
};

export default QuestionsSettings;
